import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { BRANDS as BRAND_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";

// Cafeo is warm, cozy iced-coffee — dark cocoa canvas with warm-gold accents
// and the flowing script wordmark. Bespoke hero replacing the generic BrandHero
// on the Cafeo brand page only.
// -----------------------------------------------------------------------------
const COCOA = "#2A1B0F";
const GOLD = "#F4C169";
const CREAM = "#F7EBD5";
const PINK = "#E8998C";

export default function CafeoHero({ brand, inquiryHref }) {
  const { t } = useT();
  const navigate = useNavigate();

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: COCOA, color: CREAM }}
    >
      {/* Ambient wordmark watermark */}
      <img
        aria-hidden
        src={brand.logo}
        alt=""
        className="pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[160%] max-w-none opacity-[0.05] invert brightness-200"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-20 pb-24 lg:pb-32">
        <button
          onClick={() => navigate("/#brands")}
          data-testid={BRAND_IDS.backHome}
          className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-100 transition-opacity mb-10 opacity-60"
          style={{ color: CREAM }}
        >
          <ArrowLeft className="w-4 h-4" /> {t("brandPage.backHome")}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <p
              className="text-[0.7rem] md:text-xs font-bold uppercase mb-6"
              style={{ color: GOLD, letterSpacing: "0.36em" }}
            >
              {t(`brandContent.${brand.slug}.category`)}
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="font-editorial italic leading-[1.02] text-[clamp(2.75rem,6vw,5.75rem)]"
              style={{ color: CREAM }}
            >
              &ldquo;{t(`brandContent.${brand.slug}.tagline`)}&rdquo;
            </motion.h1>

            <p
              className="font-editorial italic mt-4 text-xl md:text-2xl"
              style={{ color: GOLD }}
            >
              {t("brandContent.cafeo.heroSub") ||
                "Brewed, chilled, canned. Three moods."}
            </p>

            <p
              className="mt-10 max-w-lg text-base md:text-lg leading-[1.75]"
              style={{ color: `${CREAM}dd` }}
            >
              {brand.story &&
                (t(`brandContent.${brand.slug}.story`) || brand.story)}
            </p>

            <p
              className="mt-8 text-xs font-bold uppercase"
              style={{ color: CREAM, letterSpacing: "0.3em" }}
            >
              {t("brandContent.cafeo.attr1") || "100% Arabica"}
              <span className="mx-2" style={{ color: GOLD }}>●</span>
              {t("brandContent.cafeo.attr2") || "Sugar-free"}
              <span className="mx-2" style={{ color: GOLD }}>●</span>
              {t("brandContent.cafeo.attr3") || "Ready-to-drink"}
            </p>

            <Link
              to={inquiryHref}
              data-testid={BRAND_IDS.ctaInquiry}
              className="mt-12 inline-flex items-center gap-2 rounded-full pl-6 pr-2 py-2 text-sm font-medium transition-transform hover:scale-[1.02] shadow-sm"
              style={{ background: GOLD, color: COCOA }}
            >
              {t("brandPage.partnerCta")}
              <span
                className="inline-flex items-center justify-center w-9 h-9 rounded-full"
                style={{ background: COCOA, color: GOLD }}
              >
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Link>
          </div>

          {/* Right — Dark Chocolate Truffle can */}
          <div className="lg:col-span-6 relative flex flex-col items-center">
            <motion.img
              initial={{ opacity: 0, y: 30, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              src={brand.frontImage || brand.image}
              alt="Cafeo can"
              data-testid={BRAND_IDS.deckImage(brand.slug, 0)}
              className="max-h-[500px] w-auto mx-auto drop-shadow-[0_30px_60px_rgba(244,193,105,0.25)]"
            />

            <div
              className="mt-6 h-px w-24"
              style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
            />

            <p
              className="mt-4 text-xs font-bold uppercase"
              style={{ color: CREAM, letterSpacing: "0.3em" }}
            >
              {t("brandContent.cafeo.skuCount") || "3 Moods"}
              <span className="mx-2" style={{ color: PINK }}>●</span>
              {t("brandContent.cafeo.skuFormat") || "250 ml Can"}
              <span className="mx-2" style={{ color: PINK }}>●</span>
              {t("brandContent.cafeo.skuUse") || "Chilled"}
            </p>
          </div>
        </div>
      </div>

      <div
        className="h-[3px] w-full"
        style={{
          background: `linear-gradient(90deg, transparent 5%, ${GOLD} 20%, ${GOLD} 80%, transparent 95%)`,
        }}
      />
    </section>
  );
}
